import { Checkbox, SxProps, Theme, Box, } from '@mui/material';
import React, { useState, useEffect } from 'react';

interface BookingFormCheckboxProps {
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  [key: string]: any;
}

const boxSize = 24;

const CustomIcon = ({ checked }: { checked?: boolean }) => (
  <Box
    sx={{
      width: boxSize,
      height: boxSize,
      borderRadius: 2, // 8px
      border: `2px solid ${checked ? '#D29B5F' : '#C4C4C4'}`,
      background: checked ? '#D29B5F' : '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
    }}
  >
    {checked && (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M4 8.5L7 11.5L12 6.5"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </Box>
);

const BookingFormCheckbox: React.FC<BookingFormCheckboxProps> = ({
  checked,
  onChange,
  sx = {},
  children,
  ...props
}) => {
  // Internal state for uncontrolled mode
  const [internalChecked, setInternalChecked] = useState(!!checked);

  // Sync internal state if controlled
  useEffect(() => {
    if (typeof checked === 'boolean') {
      setInternalChecked(checked);
    }
  }, [checked]);

  const isChecked = typeof checked === 'boolean' ? checked : internalChecked;

  const handleBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const newChecked = !isChecked;
    setInternalChecked(newChecked);
    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: { checked: newChecked } as HTMLInputElement,
        currentTarget: { checked: newChecked } as HTMLInputElement,
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, newChecked);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #E6E6E6',
        py: 1,
        width: '100%',
        cursor: 'pointer',
        ...sx,
      }}
      onClick={handleBoxClick}
      role="checkbox"
      aria-checked={isChecked}
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          handleBoxClick(e as any);
        }
      }}
    >
      <Checkbox
        checked={isChecked}
        onChange={onChange}
        icon={<CustomIcon />}
        checkedIcon={<CustomIcon checked />}
        sx={{
          pb: theme => theme.spacing(2),
          mr: theme => theme.spacing(2),
          '&:hover': {
            background: 'transparent',
          },
        }}
        {...props}
      />
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
};

export default BookingFormCheckbox;